'use client';
/**
 * AlertsContext — central state for the Action Monitor UI layer:
 * - `alertsEnabled` (localStorage-persisted master toggle, the kill switch)
 * - `drawerOpen` + `symbolFilter` (controls the slide-in alert center)
 * - `seenAlertIds` (per-session set of alert ids that have already popped
 *   a toast; prevents re-pop on every poll while the alert is active)
 *
 * Steve directive 2026-05-23: master toggle lives directly under the
 * "Top 15 Q" button on the 3D scatter overlay. When OFF, every UI surface
 * (inline dots, AppNav chip, toast stack, auto-pop on new alerts) becomes
 * a no-op. The cron keeps running so flipping back ON immediately surfaces
 * what's been firing.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import type { ActionAlert } from '@/lib/action-types';

const STORAGE_KEY = 'triumph.alertsEnabled';

interface AlertsContextValue {
  /** Master kill switch. Persisted to localStorage. */
  alertsEnabled: boolean;
  setAlertsEnabled: (next: boolean) => void;

  /** Slide-in drawer state. */
  drawerOpen: boolean;
  openDrawer: (symbolFilter?: string | null) => void;
  closeDrawer: () => void;

  /** When set, drawer filters to a single symbol (e.g. when opened by
   *  clicking a SignalDot on a row). */
  symbolFilter: string | null;
  clearSymbolFilter: () => void;

  /** Per-session memory of alert ids that already triggered a toast.
   *  Reload clears it (so morning re-pops once on first visit, which is
   *  desired behavior for a PM sitting down at the desk). */
  markSeen: (id: string) => void;
  hasSeen: (id: string) => boolean;
  /** Reset all seen ids — used by the toast stack at session start to
   *  initialize from the current active set without popping a flood. */
  resetSeen: (ids: string[]) => void;

  /** New-alert callback that the toast stack subscribes to. Called once
   *  per never-before-seen alert. */
  onNewAlert: (cb: (alert: ActionAlert) => void) => () => void;
  notifyNewAlert: (alert: ActionAlert) => void;
}

const Ctx = createContext<AlertsContextValue | null>(null);

export function AlertsProvider({ children }: { children: ReactNode }) {
  // Default ON. We err on the side of feature-visible so Steve can evaluate;
  // he can flip OFF from the toggle if it's noisy.
  const [alertsEnabled, setAlertsEnabledState] = useState<boolean>(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [symbolFilter, setSymbolFilter] = useState<string | null>(null);
  const seenRef = useRef<Set<string>>(new Set());
  const subscribersRef = useRef<Set<(alert: ActionAlert) => void>>(new Set());

  // Restore alertsEnabled from localStorage on mount. The setState below
  // bridges external storage → React state; same exception as useResource.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (stored === '0') setAlertsEnabledState(false);
      else if (stored === '1') setAlertsEnabledState(true);
    } catch { /* SSR or storage disabled — ignore */ }
  }, []);

  // Deep-link auto-open: `?alerts=open` pops the drawer on mount.
  // Used by the live-presentation deck's Alerts card.
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('alerts') === 'open') {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDrawerOpen(true);
      }
    } catch { /* SSR or no window — ignore */ }
  }, []);

  const setAlertsEnabled = useCallback((next: boolean) => {
    setAlertsEnabledState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
  }, []);

  const openDrawer = useCallback((sym?: string | null) => {
    setSymbolFilter(sym ?? null);
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const clearSymbolFilter = useCallback(() => setSymbolFilter(null), []);

  const markSeen = useCallback((id: string) => { seenRef.current.add(id); }, []);
  const hasSeen = useCallback((id: string) => seenRef.current.has(id), []);
  const resetSeen = useCallback((ids: string[]) => {
    seenRef.current = new Set(ids);
  }, []);

  const onNewAlert = useCallback((cb: (alert: ActionAlert) => void) => {
    subscribersRef.current.add(cb);
    return () => { subscribersRef.current.delete(cb); };
  }, []);
  const notifyNewAlert = useCallback((alert: ActionAlert) => {
    if (!alertsEnabled) return;
    if (seenRef.current.has(alert.id)) return;
    seenRef.current.add(alert.id);
    subscribersRef.current.forEach(cb => cb(alert));
  }, [alertsEnabled]);

  const value = useMemo<AlertsContextValue>(() => ({
    alertsEnabled,
    setAlertsEnabled,
    drawerOpen,
    openDrawer,
    closeDrawer,
    symbolFilter,
    clearSymbolFilter,
    markSeen,
    hasSeen,
    resetSeen,
    onNewAlert,
    notifyNewAlert,
  }), [
    alertsEnabled, setAlertsEnabled,
    drawerOpen, openDrawer, closeDrawer,
    symbolFilter, clearSymbolFilter,
    markSeen, hasSeen, resetSeen,
    onNewAlert, notifyNewAlert,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAlerts() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAlerts must be used inside <AlertsProvider>');
  return v;
}
